///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2025
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect, useRef, forwardRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router";
import Slider from "react-slick";
import { add, parseISO } from "date-fns";
import { isEqual } from "lodash";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import {
  initialState,
  setFullScreenMode,
  fetchLoadHistory
} from "../../../slices/analyticsSlice";
import CustomDatePicker from "../../form-controls/CustomDatePicker";
import CloseFullScreenIcon from "../../icons/CloseFullScreenIcon";
import OpenFullScreenIcon from "../../icons/OpenFullScreenIcon";
import messages from "../../../helpers/messages";
import AutoComplete from "../../form-controls/AutoComplete";
import {
  analyticsFilterRankTypes,
  filterEquipmentTypes,
  loadAttributeTypes,
  patterns,
  analyticsDateRangeOptions,
  analyticsForecastedRangeOptions,
  division as divisionType
} from "../../../helpers/types";
import OutlinedInput from "../../form-controls/OutlinedInput";
import { getLocations } from "../../../api/locations";
import {
  handlePricingAnalyticsValidation,
  removePricingAnalyticsValidation
} from "../../../helpers/handlePricingAnalyticsValidation";
import PrevNextButtonIcon from "../../icons/PrevNextButtonIcon";
import getEquipmentsForAnalyticsFilters from "../../../helpers/getEquipmentsForAnalyticsFilters";

export const AnalyticsFiltersWithSlider = ({ division }) => {
  const initialFilterState = { ...initialState.filters, equipments: getEquipmentsForAnalyticsFilters(division, initialState.filters.equipments) };
  const [filters, setFilters] = useState(initialFilterState);
  const [validationError, setValidationError] = useState(initialFilterState);

  const dispatch = useDispatch();

  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);

  const route = useLocation().pathname.split("/")[1];

  const {
    analytics: { isFullScreenMode, filters: analyticsFilters, isFailure },
    bidSummary: { summary }
  } = useSelector(state => state);

  useEffect(() => {
    const updatedAnalyticsFilters = { ...analyticsFilters, equipments: getEquipmentsForAnalyticsFilters(division, analyticsFilters.equipments) };
    setFilters({ ...updatedAnalyticsFilters });
    setValidationError({ ...updatedAnalyticsFilters });
  }, [analyticsFilters, division]);

  const isValidFilters = (filters, division) => {
    var validationResult = handlePricingAnalyticsValidation(
      filters,
      division,
      validationError,
      true
    );
    setValidationError(validationResult.validationData);
    return validationResult.allValid;
  };

  const handleOnFocus = field => {
    var validationResult = removePricingAnalyticsValidation(
      validationError,
      field
    );
    setValidationError(validationResult.validationData);
  };

  const handleChange = (value, field, pattern, numeric = false) => {
    if (!pattern || value.match(pattern)) {
      const convertedValue = numeric && value === "" ? null : parseFloat(value);
      setFilters({
        ...filters,
        [field]: numeric ? convertedValue : value
      });
    }
  };

  const mapLocationOptions = options =>
    options.map(o => {
      const latitude = o.latitude ?? o.Latitude;
      const longitude = o.longitude ?? o.Longitude;
      return {
        ...o,
        latitude,
        longitude,
        label:
          o.cityName +
          (o.stateCode ? ", " + o.stateCode : "") +
          (o.countryCode2 ? ", " + o.countryCode2 : "")
      };
    });

  const handleHistoricalRangeChange = option => {
    const customMonths = -1;
    if (option.months !== customMonths) {
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - option.months);

      setFilters({
        ...filters,
        startDate: startDate.toISOString(),
        monthRange: option,
        endDate: null
      });
    } else {
      setFilters({ ...filters, monthRange: option });
    }
  };

  const handleForecastedRangeChange = option => {
    const customMonths = -1;
    if (option.months !== customMonths) {
      setFilters({
        ...filters,
        forecastStartDate: today.toISOString(),
        forecastedRange: option,
        forecastEndDate: add(new Date(), {
          months: option.months
        }).toISOString()
      });
    } else {
      setFilters({ ...filters, forecastedRange: option });
    }
  };

  const PrevButton = forwardRef((props, ref) => {
    const { className, style, onClick } = props;

    return (
      <button
        className={`${className} round`}
        style={{ ...style }}
        onClick={onClick}
        disabled={className?.includes("slick-disabled")}
        ref={ref}
        title={messages.previous.defaultMessage}
      >
        <PrevNextButtonIcon height={12} width={8} className="pos-relative" />
      </button>
    );
  });

  const NextButton = forwardRef((props, ref) => {
    const { className, style, onClick } = props;

    return (
      <button
        className={`${className} round`}
        style={{ ...style }}
        onClick={onClick}
        disabled={className?.includes("slick-disabled")}
        ref={ref}
        title={messages.next.defaultMessage}
      >
        <PrevNextButtonIcon
          height={12}
          width={8}
          className="pos-relative"
          rotateDegree={180}
        />
      </button>
    );
  });

  const {
    startDate,
    endDate,
    equipments,
    ranks,
    loadAttributes,
    origin,
    originRadius,
    destination,
    destinationRadius,
    monthRange,
    forecastStartDate,
    forecastEndDate,
    forecastedRange
  } = filters;

  const today = new Date();
  const startMinDate = new Date();
  startMinDate.setFullYear(today.getFullYear() - 2);
  const forecastMaxDate = add(new Date(), {
    months: 18
  });

  const settings = {
    infinite: false,
    slidesToShow: 9,
    slidesToScroll: 9,
    variableWidth: true,
    draggable: false,
    useCSS: false,
    prevArrow: <PrevButton ref={prevButtonRef} />,
    nextArrow: <NextButton ref={nextButtonRef} />
  };

  return (
    <div
      className="bg-white analytics-filters pt-4 pl-8 pos-relative"
      data-testid="analytics-filters"
    >
      <div
        style={{
          maxWidth: `${division === divisionType.coy ? "79vw" : "94vw"}`,
          maxHeight: "100vh"
        }}
      >
        <Slider
          {...settings}
          arrows={division === divisionType.coy ? true : false}
        >
          <div
            className="grid__cell p-4 pos-relative"
            style={{ width: "6.142%" }}
          >
            <CustomDatePicker
              value={startDate ? parseISO(startDate) : null}
              label={messages.startDate.defaultMessage}
              minDate={startMinDate}
              maxDate={endDate ? parseISO(endDate) : null}
              onChange={value =>
                setFilters({
                  ...filters,
                  monthRange: analyticsDateRangeOptions.find(i => i.custom),
                  startDate: value ? value : null
                })
              }
              id="start-date"
            />
          </div>
          <div
            className="grid__cell p-4 pos-relative"
            style={{ width: "6.142%" }}
          >
            <CustomDatePicker
              value={endDate ? parseISO(endDate) : null}
              label={messages.endDate.defaultMessage}
              minDate={startDate ? parseISO(startDate) : null}
              maxDate={today}
              onChange={value =>
                setFilters({
                  ...filters,
                  monthRange: analyticsDateRangeOptions.find(i => i.custom),
                  endDate: value ? value : null
                })
              }
              id="end-date"
            />
          </div>
          <div
            className="grid__cell p-4 pos-relative historical-range"
            style={{ width: "7.333%" }}
          >
            <AutoComplete
              value={monthRange}
              label={messages.historicalRange.defaultMessage}
              staticOptions={analyticsDateRangeOptions}
              handleChange={handleHistoricalRangeChange}
            />
          </div>
          {division === divisionType.coy && (
            <div
              className="grid__cell p-4 pos-relative"
              style={{ width: "7.142%" }}
            >
              <CustomDatePicker
                value={forecastStartDate ? parseISO(forecastStartDate) : null}
                label={messages.forecastStartDate.defaultMessage}
                minDate={today}
                maxDate={
                  forecastEndDate ? parseISO(forecastEndDate) : forecastMaxDate
                }
                onChange={value =>
                  setFilters({
                    ...filters,
                    forecastedRange: analyticsForecastedRangeOptions.find(
                      i => i.custom
                    ),
                    forecastStartDate: value ? value : null
                  })
                }
                id="forecast-start-date"
              />
            </div>
          )}
          {division === divisionType.coy && (
            <div
              className="grid__cell p-4 pos-relative"
              style={{ width: "7.142%" }}
            >
              <CustomDatePicker
                value={forecastEndDate ? parseISO(forecastEndDate) : null}
                label={messages.forecastEndDate.defaultMessage}
                minDate={
                  forecastStartDate ? parseISO(forecastStartDate) : today
                }
                maxDate={forecastMaxDate}
                onChange={value =>
                  setFilters({
                    ...filters,
                    forecastedRange: analyticsForecastedRangeOptions.find(
                      i => i.custom
                    ),
                    forecastEndDate: value ? value : null
                  })
                }
                id="forecast-end-date"
                onKeyDown={e => {
                  if (e.keyCode === 9 && e.shiftKey) {
                    prevButtonRef.current?.click();
                  }
                }}
              />
            </div>
          )}
          {division === divisionType.coy && (
            <div
              className="grid__cell p-4 pos-relative forecasted-range"
              style={{ width: "7.333%" }}
              id="forecasted-range"
            >
              <AutoComplete
                value={forecastedRange}
                label={messages.forecastedRange.defaultMessage}
                staticOptions={analyticsForecastedRangeOptions}
                handleChange={handleForecastedRangeChange}
              />
            </div>
          )}
          <div
            className="grid__cell p-4 pos-relative equipment"
            style={{ width: "6.333%" }}
          >
            <AutoComplete
              value={equipments}
              label={!summary?.isCoyoteBid ? messages.solution.defaultMessage : messages.equipment.defaultMessage}
              staticOptions={filterEquipmentTypes.filter(
                x =>
                  !x.division ||
                  (summary?.division
                    ? x.division === summary?.division
                    : x.division === division)
              )}
              handleChange={value => handleChange(value, "equipments")}
              isMulti={true}
              error={validationError.equipments?.isError}
              onFocus={() => handleOnFocus("equipments")}
            />
          </div>
          <div
            className="grid__cell p-4 pos-relative rank"
            style={{ width: "7%" }}
          >
            <AutoComplete
              value={ranks}
              label={messages.rank.defaultMessage}
              staticOptions={analyticsFilterRankTypes}
              handleChange={value => handleChange(value, "ranks")}
              isMulti={true}
              error={validationError.ranks?.isError}
              onFocus={() => handleOnFocus("ranks")}
            />
          </div>
          <div
            className="grid__cell p-4 pos-relative load-attributes"
            style={{ width: "7.3%" }}
          >
            <AutoComplete
              value={loadAttributes}
              label={messages.loadAttributes.defaultMessage}
              staticOptions={loadAttributeTypes}
              handleChange={value => handleChange(value, "loadAttributes")}
              isMulti={true}
            />
          </div>
          <div
            className="grid__cell p-4 pos-relative origin"
            style={{ width: "7.333%" }}
          >
            <AutoComplete
              value={origin}
              onChange={value => handleChange(value, "origin")}
              fetchFrom={getLocations}
              label={messages.origin.defaultMessage}
              handleOptionsMapping={mapLocationOptions}
              filterDynamicOptions={false}
              error={validationError.origin?.isError}
              onFocus={() => handleOnFocus("origin")}
              onKeyDown={e => {
                if (e.keyCode === 9 && !e.shiftKey) {
                  nextButtonRef.current?.click();
                }
              }}
            />
          </div>
          <div
            className="grid__cell p-4 pos-relative origin-radius"
            style={{ width: "6.5%" }}
          >
            <OutlinedInput
              value={originRadius ?? ""}
              onChange={({ target }) =>
                handleChange(
                  target.value,
                  "originRadius",
                  patterns.intThree,
                  true
                )
              }
              label={messages.originRadius.defaultMessage}
              error={validationError.originRadius?.isError}
              onFocus={() => handleOnFocus("originRadius")}
            />
          </div>
          <div
            className="grid__cell p-4 pos-relative destination"
            style={{ width: "7.333%" }}
          >
            <AutoComplete
              value={destination}
              onChange={value => handleChange(value, "destination")}
              fetchFrom={getLocations}
              label={messages.destination.defaultMessage}
              handleOptionsMapping={mapLocationOptions}
              filterDynamicOptions={false}
              error={validationError.destination?.isError}
              onFocus={() => handleOnFocus("destination")}
            />
          </div>
          <div
            className="grid__cell p-4 pos-relative destination-radius"
            style={{ width: "6.5%" }}
          >
            <OutlinedInput
              value={destinationRadius ?? ""}
              onChange={({ target }) =>
                handleChange(
                  target.value,
                  "destinationRadius",
                  patterns.intThree,
                  true
                )
              }
              label={messages.destRadius.defaultMessage}
              error={validationError.destinationRadius?.isError}
              onFocus={() => handleOnFocus("destinationRadius")}
            />
          </div>
        </Slider>
      </div>
      <button
        data-testid="apply"
        className="h-32 text-button univers-67-bold-cond mt-6 short-button ml-12 apply-button pos-absolute"
        disabled={isEqual(filters, analyticsFilters)}
        onClick={() => {
          if (isValidFilters(filters, division))
            dispatch(fetchLoadHistory({ division, analyticsFilters: filters }));
        }}
      >
        {messages.apply.defaultMessage}
      </button>
      {route !== "analytics" && (
        <button
          className={
            "fr no-focus mr-40 mt-12 screen-mode pos-absolute" +
            (isFailure ? " disabled" : "")
          }
          data-testid="toggle-pricing-grid"
          onClick={() => dispatch(setFullScreenMode(!isFullScreenMode))}
          disabled={isFailure}
        >
          {isFullScreenMode ? (
            <CloseFullScreenIcon height={18} width={18} />
          ) : (
            <OpenFullScreenIcon
              height={18}
              width={18}
              fill={isFailure ? "#ADAFB2" : "#000"}
            />
          )}
        </button>
      )}
    </div>
  );
};

export default AnalyticsFiltersWithSlider;
